import { inject, Pipe, type PipeTransform } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { toSentenceCase } from '@quipex/shared/utils';
import { formatRelative, type Locale } from 'date-fns';

@Pipe({
    name: 'qpxRelativeDate',
})
export class RelativeDatePipe implements PipeTransform {
    private readonly locale = inject(MAT_DATE_LOCALE);

    /**
     * Transforms a date into a relative date string (e.g., "today at 3:00 PM", "last Wednesday at 5:30 PM").
     *
     * Uses date-fns formatRelative with the application's configured date locale.
     *
     * @param value - The date to format
     * @param baseDate - The reference date to compare against (defaults to current time)
     * @returns Formatted relative date string
     */
    transform(value: Date, baseDate: Date = new Date()): string {
        if (!(value instanceof Date)) return '';

        const options = isDateFnsLocale(this.locale)
            ? { locale: this.locale }
            : undefined;

        return toSentenceCase(formatRelative(value, baseDate, options), true);
    }
}

function isDateFnsLocale(value: unknown): value is Locale {
    return (
        typeof value === 'object' &&
        value !== null &&
        'formatRelative' in value &&
        typeof value.formatRelative === 'function' &&
        'formatLong' in value &&
        typeof value.formatLong === 'object' &&
        'localize' in value &&
        typeof value.localize === 'object'
    );
}
